import { showToast } from "controls/global/stewart-toast";
import { StageType } from "utils/data/enum";
import {
  insertPolicyTransactionStage,
  savePolicyTransaction,
} from "utils/api/policyTransactionApi";

const QUEUE_NAME: Partial<Record<StageType, string>> = {
  [StageType.PRECLOSE]: "Pre-Close",
  [StageType.CLOSE]: "Close",
};

// Force-promote the given records to the next stage, then toast the outcome.
// identityKeys are PolicyTransaction IdentityKeys; stage is the destination
// (PRECLOSE from Quality Review, CLOSE from Pre-Close Queue).
//
// Pass isForcePromoted 0 to promote without the force flag: the record has no
// open errors, so this is an ordinary stage change rather than an override.
// BUS_StageInsert then advances the stage but skips the FORCE_PROMOTED note and
// activity rows, and leaves the record's IsForcePromoted flag alone.
export async function forcePromoteRecords(
  identityKeys: number[],
  stage: StageType,
  reason: string,
  isForcePromoted: 0 | 1 = 1
): Promise<boolean> {
  const ok = await insertPolicyTransactionStage({
    PolicyTransactionListID: identityKeys.join(","),
    IsForcePromoted: isForcePromoted,
    StageTypeCode: stage,
    StageChangeReason: reason,
  });
  const queueName = QUEUE_NAME[stage] ?? "Pre-Close";
  const n = identityKeys.length;
  // Match the wording to the button the user clicked — a plain promote must not
  // report itself as a force promote.
  const pastTense = isForcePromoted ? "force promoted" : "promoted";
  const infinitive = isForcePromoted ? "force promote" : "promote";
  if (ok) {
    showToast({
      type: "success",
      message: `${n} record${n !== 1 ? "s" : ""} ${pastTense} to ${queueName} queue`,
    });
  } else {
    showToast({
      type: "error",
      message: `Failed to ${infinitive} the selected records to the ${queueName} queue`,
    });
  }
  return ok;
}

// Remove the force-promote flag from a single record via SavePolicyTransaction,
// then toast the outcome.
export async function removeForcePromoteRecord(
  identityKey: number
): Promise<boolean> {
  const ok = await savePolicyTransaction({
    IdentityKey: identityKey,
    IsForcePromoted: false,
  });
  if (ok) {
    showToast({ type: "success", message: "Force Promoted has been removed" });
  } else {
    showToast({ type: "error", message: "Failed to remove Force Promoted" });
  }
  return ok;
}
